// src/lib/data/sync/ConflictResolver.js - V4 ENTERPRISE ULTRA ROBUSTO
// ═══════════════════════════════════════════════════════════════════════════
// 🔄 CONFLICT RESOLVER — Airbnb / Zillow / QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V4: INDEXEDDB MIGRATION — Logs ahora usan IndexedDB (sin límite de cuota)
// 🔥 V4: _safeSetItem → saveConflictLogEntry (IndexedDB)
// 🔥 V4: loadConflictLog → getConflictLogEntry (IndexedDB)
// 🔥 V4: saveManualConflict → saveConflictLogEntry (IndexedDB)
// 🔥 V4: COMPATIBLE HACIA ATRÁS — Si IndexedDB falla, usa localStorage
// 🔥 V3: STORAGE SAFE — maxLogSize reducido (100→15) para evitar cuota llena
// 🔥 V3: STORAGE SAFE — saveConflictLog sanitiza y trunca datos grandes
// 🔥 V3: STORAGE SAFE — Limpieza automática de logs > 7 días
// 🔥 V2: REGLA ABSOLUTA - Delete SIEMPRE vence sobre cualquier dato del cloud
// 🔥 V2: Verificación de tombstone ANTES de cualquier estrategia
// ═══════════════════════════════════════════════════════════════════════════

import { saveConflictLogEntry, getConflictLogEntry } from '../../storage/indexedDB.js';

class ConflictResolver {
  constructor(userId, localStrategy = null) {
    this.userId = userId;
    this.localStrategy = localStrategy;
    this.strategies = this.getDefaultStrategies();
    this.versionField = '_version';
    this.timestampField = '_lastUpdated';
    this.conflictLog = [];
    
    // 🔥 V3: Reducido de 100 a 15 para evitar saturar localStorage
    this.maxLogSize = 15;
    
    // 🔥 V4: Flag para saber si IndexedDB está disponible
    this._indexedDBAvailable = null;
    
    // 🔥 V2: Caché local de tombstones
    this._tombstoneCheckCache = new Map();
    this._tombstoneCacheTTL = 60000;
    
    // 🔥 V4: Cargar logs desde IndexedDB (asíncrono, no bloquea el constructor)
    this._loadLogsAsync();
    
    console.log(`🔄 ConflictResolver V4 inicializado para usuario ${userId} | DELETE_PRIORITY | INDEXEDDB_LOGS`);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 V4: CARGA ASÍNCRONA DE LOGS DESDE INDEXEDDB
  // ═══════════════════════════════════════════════════════════════════════

  async _loadLogsAsync() {
    try {
      const saved = await getConflictLogEntry(`conflict_log_${this.userId}`);
      if (saved && saved.log) {
        this.conflictLog = Array.isArray(saved.log) ? saved.log.slice(0, this.maxLogSize) : [];
        console.log(`📋 [V4] ${this.conflictLog.length} logs cargados desde IndexedDB`);
      }
    } catch (error) {
      console.warn('⚠️ [V4] No se pudieron cargar logs desde IndexedDB, usando localStorage fallback');
      this._loadLogsFromLocalStorage();
    }
  }

  /**
   * 🔥 V4: Fallback a localStorage si IndexedDB no está disponible.
   * Garantiza compatibilidad hacia atrás sin romper nada.
   */
  _loadLogsFromLocalStorage() {
    try {
      const saved = localStorage.getItem(`conflict_log_${this.userId}`);
      if (saved) {
        const { log } = JSON.parse(saved);
        this.conflictLog = Array.isArray(log) ? log.slice(0, this.maxLogSize) : [];
      }
    } catch (error) {
      console.error('Error loading conflict log from localStorage:', error);
      this.conflictLog = [];
    }
  }

  /**
   * 🔥 V4: Verifica si IndexedDB está disponible (con caché de resultado).
   * Solo intenta la verificación una vez para evitar overhead.
   */
  async _isIndexedDBAvailable() {
    if (this._indexedDBAvailable !== null) return this._indexedDBAvailable;
    
    try {
      // Prueba rápida: intentar guardar y leer un valor pequeño
      const testResult = await saveConflictLogEntry(`conflict_log_test_${this.userId}`, '{}');
      this._indexedDBAvailable = testResult === true;
    } catch {
      this._indexedDBAvailable = false;
    }
    
    return this._indexedDBAvailable;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🛡️ V4: ESCRITURA SEGURA (INDEXEDDB CON FALLBACK A LOCALSTORAGE)
  // ═══════════════════════════════════════════════════════════════════════

  async _safeSetItem(key, value) {
    // 🔥 V4: Intentar IndexedDB primero
    try {
      const indexedDBAvailable = await this._isIndexedDBAvailable();
      if (indexedDBAvailable) {
        const success = await saveConflictLogEntry(key, value);
        if (success) return true;
      }
    } catch (e) {
      console.warn('⚠️ [V4] IndexedDB no disponible para _safeSetItem, usando localStorage:', e.message);
    }

    // 🔥 FALLBACK: localStorage (comportamiento original V3)
    if (typeof window === 'undefined' || !window.localStorage) return false;
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      if (e.name === 'QuotaExceededError' || 
          e.message?.includes('exceeded the quota') ||
          e.message?.includes('Setting the value')) {
        console.warn('⚠️ [ConflictResolver V4] Cuota excedida — limpiando logs propios...');
        // Limpiar solo los logs de este usuario
        try {
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && (k.startsWith(`conflict_log_${this.userId}`) || 
                     k.startsWith(`manual_conflicts_${this.userId}`))) {
              keysToRemove.push(k);
            }
          }
          keysToRemove.forEach(k => { try { localStorage.removeItem(k); } catch (_) {} });
        } catch (_) {}
        
        // Reintentar
        try {
          localStorage.setItem(key, value);
          return true;
        } catch (e2) {
          console.error('❌ [ConflictResolver V4] No se pudo guardar:', key.substring(0, 50));
          return false;
        }
      }
      throw e;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ESTRATEGIAS POR DEFECTO
  // ═══════════════════════════════════════════════════════════════════════

  getDefaultStrategies() {
    return {
      default: 'LAST_WRITE_WINS',
      clients: 'LAST_WRITE_WINS',
      properties: 'MERGE_WITH_PRIORITY',
      tasks: 'CLIENT_WINS',
      calendarEvents: 'SERVER_WINS',
      documents: 'SERVER_WINS',
      financeiro: 'MANUAL_REVIEW',
      company: 'SERVER_WINS',
      userPreferences: 'CLIENT_WINS',
      fieldStrategies: {
        'password': 'SERVER_WINS',
        'price': 'HIGHER_WINS',
        'status': 'PRIORITY_QUEUE',
        '_deleted': 'DELETE_ALWAYS_WINS',
        '_tombstone': 'DELETE_ALWAYS_WINS'
      }
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 V2: DELETE PRIORITY
  // ═══════════════════════════════════════════════════════════════════════

  preResolveDeleteCheck(localData, remoteData, collection) {
    const localDeleted = this._isDeleted(localData);
    const remoteDeleted = this._isDeleted(remoteData);
    
    if (localDeleted && remoteDeleted) {
      return {
        resolvedData: {
          ...localData,
          _deleted: true,
          _deletedAt: localData._deletedAt || remoteData._deletedAt || new Date().toISOString(),
          _deletedBy: localData._deletedBy || this.userId,
          [this.versionField]: Math.max(localData[this.versionField] || 0, remoteData[this.versionField] || 0) + 1,
          [this.timestampField]: new Date().toISOString()
        },
        resolutionMethod: 'DELETE_PRIORITY_BOTH_DELETED'
      };
    }
    
    if (localDeleted && !remoteDeleted) {
      return {
        resolvedData: {
          ...localData,
          _deleted: true,
          _deletedAt: localData._deletedAt || new Date().toISOString(),
          _deletedBy: localData._deletedBy || this.userId,
          _deleteSource: 'local',
          [this.versionField]: Math.max(localData[this.versionField] || 0, remoteData[this.versionField] || 0) + 1,
          [this.timestampField]: new Date().toISOString()
        },
        resolutionMethod: 'DELETE_PRIORITY_LOCAL_WINS'
      };
    }
    
    if (remoteDeleted && !localDeleted) {
      return {
        resolvedData: {
          ...remoteData,
          _deleted: true,
          _deletedAt: remoteData._deletedAt || new Date().toISOString(),
          _deletedBy: remoteData._deletedBy || 'cloud',
          _deleteSource: 'remote',
          _preDeleteLocalSnapshot: {
            id: localData.id,
            updatedAt: localData.updatedAt || localData[this.timestampField]
          },
          [this.versionField]: Math.max(localData[this.versionField] || 0, remoteData[this.versionField] || 0) + 1,
          [this.timestampField]: new Date().toISOString()
        },
        resolutionMethod: 'DELETE_PRIORITY_REMOTE_WINS'
      };
    }
    
    return null;
  }

  _isDeleted(data) {
    if (!data || typeof data !== 'object') return false;
    if (data._deleted === true) return true;
    if (data._tombstone === true) return true;
    if (data.deleted === true) return true;
    if (data.status === 'deleted') return true;
    if (data._syncStatus === 'deleted') return true;
    if (data._deletedAt) {
      const deleteTime = new Date(data._deletedAt).getTime();
      if (!isNaN(deleteTime) && deleteTime > 0) return true;
    }
    return false;
  }

  async _hasActiveTombstone(collection, recordId) {
    if (!collection || !recordId) return false;
    const cacheKey = `${collection}:${recordId}`;
    const cached = this._tombstoneCheckCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this._tombstoneCacheTTL) {
      return cached.result;
    }
    try {
      if (this.localStrategy && typeof this.localStrategy.isTombstoned === 'function') {
        const result = await this.localStrategy.isTombstoned(collection, recordId);
        this._tombstoneCheckCache.set(cacheKey, { result, timestamp: Date.now() });
        return result;
      }
    } catch (error) {
      console.warn('⚠️ Error verificando tombstone:', error.message);
    }
    return false;
  }

  invalidateTombstoneCache(collection, recordId) {
    if (collection && recordId) {
      this._tombstoneCheckCache.delete(`${collection}:${recordId}`);
    } else {
      this._tombstoneCheckCache.clear();
    }
  }

  setLocalStrategy(localStrategy) {
    this.localStrategy = localStrategy;
    console.log('✅ ConflictResolver V4: LocalStrategy vinculada');
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔄 RESOLUCIÓN PRINCIPAL
  // ═══════════════════════════════════════════════════════════════════════

  async resolve(collection, localData, remoteData) {
    const deleteCheckResult = this.preResolveDeleteCheck(localData, remoteData, collection);
    if (deleteCheckResult) {
      const resolvedWithMeta = this.addResolutionMetadata(deleteCheckResult.resolvedData, {
        collection,
        strategy: 'DELETE_PRIORITY',
        method: deleteCheckResult.resolutionMethod,
        localVersion: localData[this.versionField] || 0,
        remoteVersion: remoteData[this.versionField] || 0,
        resolvedAt: new Date().toISOString(),
        resolvedBy: this.userId,
        deletePriorityApplied: true
      });
      
      this.logConflict({
        collection,
        itemId: localData.id || remoteData.id,
        strategy: 'DELETE_PRIORITY',
        resolutionMethod: deleteCheckResult.resolutionMethod,
        localData: this.sanitizeForLog(localData),
        remoteData: this.sanitizeForLog(remoteData),
        resolvedData: this.sanitizeForLog(resolvedWithMeta),
        timestamp: new Date().toISOString(),
        deletePriority: true
      });
      
      return resolvedWithMeta;
    }
    
    const recordId = localData?.id || remoteData?.id;
    if (recordId) {
      const hasTombstone = await this._hasActiveTombstone(collection, recordId);
      if (hasTombstone) {
        return {
          ...localData,
          _deleted: true,
          _tombstone: true,
          _deletedAt: localData._deletedAt || new Date().toISOString(),
          _deletedBy: localData._deletedBy || this.userId,
          _deleteSource: 'tombstone',
          [this.versionField]: Math.max(localData[this.versionField] || 0, remoteData[this.versionField] || 0) + 1,
          [this.timestampField]: new Date().toISOString(),
        };
      }
    }
    
    const strategy = this.getStrategyForCollection(collection);
    
    console.log(`🔄 Resolving conflict for ${collection}:`, {
      strategy,
      local: { id: localData.id, version: localData[this.versionField] },
      remote: { id: remoteData.id, version: remoteData[this.versionField] }
    });

    let resolvedData, resolutionMethod;

    switch (strategy) {
      case 'LAST_WRITE_WINS':
        ({ resolvedData, resolutionMethod } = this.lastWriteWins(localData, remoteData));
        break;
      case 'MERGE_WITH_PRIORITY':
        ({ resolvedData, resolutionMethod } = this.mergeWithPriority(localData, remoteData, collection));
        break;
      case 'CLIENT_WINS':
        ({ resolvedData, resolutionMethod } = this.clientWins(localData, remoteData));
        break;
      case 'SERVER_WINS':
        ({ resolvedData, resolutionMethod } = this.serverWins(localData, remoteData));
        break;
      case 'MANUAL_REVIEW':
        ({ resolvedData, resolutionMethod } = this.manualReview(localData, remoteData, collection));
        break;
      case 'HIGHER_WINS':
        ({ resolvedData, resolutionMethod } = this.higherWins(localData, remoteData, collection));
        break;
      case 'PRIORITY_QUEUE':
        ({ resolvedData, resolutionMethod } = this.priorityQueue(localData, remoteData, collection));
        break;
      default:
        ({ resolvedData, resolutionMethod } = this.lastWriteWins(localData, remoteData));
    }

    resolvedData = this.addResolutionMetadata(resolvedData, {
      collection,
      strategy,
      method: resolutionMethod,
      localVersion: localData[this.versionField] || 0,
      remoteVersion: remoteData[this.versionField] || 0,
      resolvedAt: new Date().toISOString(),
      resolvedBy: this.userId,
      deletePriorityApplied: false
    });

    this.logConflict({
      collection,
      itemId: localData.id || remoteData.id,
      strategy,
      resolutionMethod,
      localData: this.sanitizeForLog(localData),
      remoteData: this.sanitizeForLog(remoteData),
      resolvedData: this.sanitizeForLog(resolvedData),
      timestamp: new Date().toISOString()
    });

    return resolvedData;
  }

  resolveSync(collection, localData, remoteData) {
    const deleteCheckResult = this.preResolveDeleteCheck(localData, remoteData, collection);
    if (deleteCheckResult) {
      return this.addResolutionMetadata(deleteCheckResult.resolvedData, {
        collection,
        strategy: 'DELETE_PRIORITY',
        method: deleteCheckResult.resolutionMethod,
        localVersion: localData[this.versionField] || 0,
        remoteVersion: remoteData[this.versionField] || 0,
        resolvedAt: new Date().toISOString(),
        resolvedBy: this.userId,
        deletePriorityApplied: true
      });
    }
    
    const { resolvedData, resolutionMethod } = this.lastWriteWins(localData, remoteData);
    return this.addResolutionMetadata(resolvedData, {
      collection,
      strategy: 'LAST_WRITE_WINS',
      method: resolutionMethod,
      localVersion: localData[this.versionField] || 0,
      remoteVersion: remoteData[this.versionField] || 0,
      resolvedAt: new Date().toISOString(),
      resolvedBy: this.userId,
      deletePriorityApplied: false
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ESTRATEGIAS
  // ═══════════════════════════════════════════════════════════════════════

  lastWriteWins(localData, remoteData) {
    const localTime = new Date(localData[this.timestampField] || 0).getTime();
    const remoteTime = new Date(remoteData[this.timestampField] || 0).getTime();
    
    if (localTime >= remoteTime) {
      return {
        resolvedData: { ...localData, [this.versionField]: (localData[this.versionField] || 0) + 1 },
        resolutionMethod: 'LOCAL_WINS_TIMESTAMP'
      };
    } else {
      return {
        resolvedData: { ...remoteData, [this.versionField]: (remoteData[this.versionField] || 0) + 1 },
        resolutionMethod: 'REMOTE_WINS_TIMESTAMP'
      };
    }
  }

  mergeWithPriority(localData, remoteData, collection) {
    const resolved = { ...remoteData };
    const priorityFields = this.getPriorityFieldsForCollection(collection);
    
    priorityFields.high.forEach(field => {
      if (localData[field] !== undefined) resolved[field] = localData[field];
    });
    
    priorityFields.arrays.forEach(field => {
      if (localData[field] && remoteData[field]) {
        resolved[field] = [...new Set([...localData[field], ...remoteData[field]])];
      }
    });
    
    priorityFields.objects.forEach(field => {
      if (localData[field] && remoteData[field]) {
        resolved[field] = { ...remoteData[field], ...localData[field] };
      }
    });
    
    const maxVersion = Math.max(localData[this.versionField] || 0, remoteData[this.versionField] || 0) + 1;
    resolved[this.versionField] = maxVersion;
    resolved[this.timestampField] = new Date().toISOString();
    
    return { resolvedData: resolved, resolutionMethod: 'MERGED_WITH_PRIORITIES' };
  }

  clientWins(localData, remoteData) {
    return {
      resolvedData: { ...localData, [this.versionField]: (localData[this.versionField] || 0) + 1, _mergedFromRemote: remoteData },
      resolutionMethod: 'CLIENT_WINS_STRATEGY'
    };
  }

  serverWins(localData, remoteData) {
    return {
      resolvedData: { ...remoteData, [this.versionField]: (remoteData[this.versionField] || 0) + 1, _mergedFromLocal: localData },
      resolutionMethod: 'SERVER_WINS_STRATEGY'
    };
  }

  manualReview(localData, remoteData, collection) {
    const conflictId = `conflict_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const conflictRecord = {
      id: conflictId, collection,
      localData: this.sanitizeForLog(localData),
      remoteData: this.sanitizeForLog(remoteData),
      createdAt: new Date().toISOString(),
      status: 'pending', requiresAttention: true,
      severity: this.calculateSeverity(localData, remoteData, collection)
    };
    this.saveManualConflict(conflictRecord);
    return {
      resolvedData: { ...localData, [this.versionField]: (localData[this.versionField] || 0) + 1, _hasConflict: true, _conflictId: conflictId, _requiresManualReview: true },
      resolutionMethod: 'MANUAL_REVIEW_PENDING'
    };
  }

  higherWins(localData, remoteData, collection) {
    const valueField = this.getValueFieldForCollection(collection);
    if (!valueField) return this.lastWriteWins(localData, remoteData);
    const localValue = parseFloat(localData[valueField]) || 0;
    const remoteValue = parseFloat(remoteData[valueField]) || 0;
    if (localValue >= remoteValue) {
      return { resolvedData: { ...localData, [this.versionField]: (localData[this.versionField] || 0) + 1 }, resolutionMethod: 'LOCAL_WINS_HIGHER_VALUE' };
    } else {
      return { resolvedData: { ...remoteData, [this.versionField]: (remoteData[this.versionField] || 0) + 1 }, resolutionMethod: 'REMOTE_WINS_HIGHER_VALUE' };
    }
  }

  priorityQueue(localData, remoteData, collection) {
    const stateField = 'status';
    const priorityMap = { 'sold': 100, 'rented': 90, 'reserved': 80, 'available': 70, 'pending': 60, 'draft': 50, 'cancelled': 40, 'deleted': 0 };
    const localPriority = priorityMap[localData[stateField]] || 0;
    const remotePriority = priorityMap[remoteData[stateField]] || 0;
    if (localPriority >= remotePriority) {
      return { resolvedData: { ...localData, [this.versionField]: (localData[this.versionField] || 0) + 1 }, resolutionMethod: 'LOCAL_WINS_PRIORITY' };
    } else {
      return { resolvedData: { ...remoteData, [this.versionField]: (remoteData[this.versionField] || 0) + 1 }, resolutionMethod: 'REMOTE_WINS_PRIORITY' };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  getStrategyForCollection(collection) {
    return this.strategies[collection] || this.strategies.default;
  }

  getPriorityFieldsForCollection(collection) {
    const configs = {
      properties: { high: ['price', 'status', 'address', 'title'], medium: ['description', 'features'], low: ['notes', 'tags'], arrays: ['images', 'tags', 'features'], objects: ['metadata', 'location'] },
      clients: { high: ['name', 'email', 'phone', 'status'], medium: ['budget', 'source', 'pipeline'], low: ['notes', 'tags'], arrays: ['tags', 'interactions'], objects: ['preferences', 'address'] },
      default: { high: [], medium: [], low: [], arrays: [], objects: [] }
    };
    return configs[collection] || configs.default;
  }

  getValueFieldForCollection(collection) {
    const valueFields = { properties: 'price', financeiro: 'value', tasks: 'priorityValue', default: null };
    return valueFields[collection] || valueFields.default;
  }

  calculateSeverity(localData, remoteData, collection) {
    let severity = 1;
    const criticalFields = { properties: ['price', 'status', 'address'], financeiro: ['value', 'type', 'date'], clients: ['email', 'phone', 'name'], tasks: ['dueDate', 'priority', 'assignedTo'] };
    const fields = criticalFields[collection] || [];
    fields.forEach(field => { if (localData[field] !== remoteData[field]) severity += 2; });
    return Math.min(severity, 5);
  }

  addResolutionMetadata(data, metadata) {
    return {
      ...data,
      _syncMetadata: {
        ...(data._syncMetadata || {}),
        lastResolution: metadata,
        resolutionHistory: [...(data._syncMetadata?.resolutionHistory || []), metadata].slice(-5)
      }
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📝 LOGGING (V4: INDEXEDDB + FALLBACK LOCALSTORAGE)
  // ═══════════════════════════════════════════════════════════════════════

  logConflict(conflictInfo) {
    this.conflictLog.unshift(conflictInfo);
    
    // 🔥 V3: Limitar a maxLogSize (15)
    if (this.conflictLog.length > this.maxLogSize) {
      this.conflictLog = this.conflictLog.slice(0, this.maxLogSize);
    }
    
    // 🔥 V4: Guardar asíncronamente (no bloquea la resolución)
    this.saveConflictLog();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🛡️ V4: PERSISTIR LOG (INDEXEDDB CON FALLBACK A LOCALSTORAGE)
  // ═══════════════════════════════════════════════════════════════════════

  saveConflictLog() {
    // 🔥 V3: Sanitizar datos antes de guardar (eliminar campos pesados)
    const sanitizedLog = this.conflictLog.map(entry => ({
      collection: entry.collection,
      itemId: entry.itemId,
      strategy: entry.strategy,
      resolutionMethod: entry.resolutionMethod,
      timestamp: entry.timestamp,
      deletePriority: entry.deletePriority || false,
      // 🔥 V3: NO guardar localData, remoteData, resolvedData completos
      // Solo guardar referencias ligeras
      localSummary: entry.localData ? { id: entry.localData.id } : null,
      remoteSummary: entry.remoteData ? { id: entry.remoteData.id } : null,
    }));

    const payload = JSON.stringify({
      log: sanitizedLog,
      updatedAt: new Date().toISOString(),
      _timestamp: Date.now(),
    });

    // 🔥 V4: _safeSetItem ahora es async y usa IndexedDB primero
    this._safeSetItem(`conflict_log_${this.userId}`, payload);
  }

  saveManualConflict(conflictRecord) {
    const key = `manual_conflicts_${this.userId}`;
    let existing = [];
    try {
      // 🔥 V4: Intentar cargar desde IndexedDB primero, luego localStorage
      const raw = localStorage.getItem(key);
      existing = raw ? JSON.parse(raw) : [];
    } catch { existing = []; }
    
    existing.unshift(conflictRecord);
    
    // 🔥 V3: Máximo 5 conflictos manuales
    if (existing.length > 5) {
      existing = existing.slice(0, 5);
    }
    
    // 🔥 V4: _safeSetItem ahora usa IndexedDB
    this._safeSetItem(key, JSON.stringify(existing));
    this.dispatchConflictEvent(conflictRecord);
  }

  dispatchConflictEvent(conflictRecord) {
    try {
      const event = new CustomEvent('syncConflict', {
        detail: { type: 'MANUAL_REVIEW_REQUIRED', conflict: conflictRecord, userId: this.userId, timestamp: new Date().toISOString() }
      });
      window.dispatchEvent(event);
    } catch (_) {}
  }

  /**
   * 🔥 V4: Carga logs desde IndexedDB (método público para recarga manual).
   * La carga inicial ya ocurre en _loadLogsAsync() durante la construcción.
   */
  async loadConflictLog() {
    await this._loadLogsAsync();
  }

  /**
   * 🔥 V3: Sanitizar datos para logs (elimina base64 y campos pesados)
   */
  sanitizeForLog(data) {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    const sensitiveFields = ['password', 'token', 'creditCard', 'cpf', 'cnpj'];
    const heavyFields = ['base64', 'images', 'previewImage', 'publicImages', 'data'];
    
    // Redactar campos sensibles
    sensitiveFields.forEach(field => {
      if (sanitized[field] !== undefined) sanitized[field] = '***REDACTED***';
    });
    
    // 🔥 V3: Truncar campos pesados (base64, imágenes)
    heavyFields.forEach(field => {
      if (typeof sanitized[field] === 'string' && sanitized[field].length > 100) {
        sanitized[field] = `[TRUNCATED: ${sanitized[field].length} chars]`;
      }
      if (Array.isArray(sanitized[field]) && sanitized[field].length > 3) {
        sanitized[field] = `[ARRAY: ${sanitized[field].length} items]`;
      }
    });
    
    return sanitized;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📊 ESTADÍSTICAS Y MANTENIMIENTO
  // ═══════════════════════════════════════════════════════════════════════

  getConflictStats() {
    const byCollection = {};
    const byStrategy = {};
    
    this.conflictLog.forEach(conflict => {
      byCollection[conflict.collection] = (byCollection[conflict.collection] || 0) + 1;
      byStrategy[conflict.strategy] = (byStrategy[conflict.strategy] || 0) + 1;
    });
    
    return {
      total: this.conflictLog.length,
      byCollection,
      byStrategy,
      lastConflict: this.conflictLog[0] || null
    };
  }

  cleanupOldLogs() {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    this.conflictLog = this.conflictLog.filter(conflict => {
      const conflictTime = new Date(conflict.timestamp).getTime();
      return conflictTime > sevenDaysAgo;
    });
    this.saveConflictLog();
  }

  setStrategy(collection, strategy) {
    if (!this.isValidStrategy(strategy)) throw new Error(`Invalid strategy: ${strategy}`);
    this.strategies[collection] = strategy;
    return this.strategies;
  }

  isValidStrategy(strategy) {
    return ['LAST_WRITE_WINS', 'MERGE_WITH_PRIORITY', 'CLIENT_WINS', 'SERVER_WINS', 'MANUAL_REVIEW', 'HIGHER_WINS', 'PRIORITY_QUEUE', 'DELETE_ALWAYS_WINS'].includes(strategy);
  }
}

export default ConflictResolver;
